import { Button, PlusIcon } from "@kkp/ui";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../ui/Dialog";
import { Field, Input } from "../../../ui/Input";

type AddCompanyFormApi = ReturnType<
  typeof import("../hooks/useAddCompanyForm").useAddCompanyForm
>;

export function AddCompanyDialog({ form }: { form: AddCompanyFormApi }) {
  const { isOpen, setIsOpen, register, errors, isSubmitting, onSubmit } = form;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="primary" leftIcon={<PlusIcon />}>
          Add Company
        </Button>
      </DialogTrigger>

      <DialogContent wide className="kkp-flowbite">
        <DialogHeader className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="grid gap-1">
            <DialogTitle>Add Company</DialogTitle>
            <DialogDescription>
              Create a company record. Validation follows the blueprint.
            </DialogDescription>
          </div>
        </DialogHeader>

        <form
          className="grid gap-5 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(24,19,36,0.35)] p-4 sm:p-5"
          onSubmit={onSubmit}
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="Company legal name"
              required
              error={errors.legalName?.message}
              hint="Example: PT KamuKirim - Payroll"
            >
              <Input
                placeholder="Acme Corp"
                autoComplete="organization"
                {...register("legalName")}
              />
            </Field>

            <Field
              label="Back-office ID"
              required
              error={errors.backOfficeId?.message}
            >
              <Input placeholder="KKP/2605/001" {...register("backOfficeId")} />
            </Field>

            <Field label="Tax ID (NPWP)" required error={errors.taxId?.message}>
              <Input
                placeholder="01.234.567.8-901.000"
                {...register("taxId")}
              />
            </Field>

            <Field
              label="Legal address"
              required
              error={errors.legalAddress?.message}
              className="sm:col-span-2 lg:col-span-3"
            >
              <Input
                placeholder="Jl. Sudirman No. 1, Jakarta"
                {...register("legalAddress")}
              />
            </Field>

            <Field
              label="Source bank code"
              required
              error={errors.sourceBankCode?.message}
            >
              <Input placeholder="002" {...register("sourceBankCode")} />
            </Field>

            <Field
              label="Source bank account"
              required
              error={errors.sourceBankAccount?.message}
            >
              <Input
                placeholder="1234567890"
                {...register("sourceBankAccount")}
              />
            </Field>

            <Field
              label="Per batch limit (IDR)"
              required
              error={errors.perBatchLimitIdr?.message}
            >
              <Input
                placeholder="100000000"
                inputMode="numeric"
                {...register("perBatchLimitIdr")}
              />
            </Field>

            <Field
              label="Per transaction fee (IDR)"
              required
              error={errors.perTransactionFeeIdr?.message}
            >
              <Input
                placeholder="2500"
                inputMode="numeric"
                {...register("perTransactionFeeIdr")}
              />
            </Field>

            <Field
              label="PIC fullname"
              required
              error={errors.picFullname?.message}
            >
              <Input placeholder="Rama Wirawan" {...register("picFullname")} />
            </Field>

            <Field
              label="PIC phonenumber"
              required
              error={errors.picPhonenumber?.message}
            >
              <Input
                placeholder="+62 812 0000 1234"
                inputMode="tel"
                {...register("picPhonenumber")}
              />
            </Field>

            <Field label="PIC email" required error={errors.picEmail?.message}>
              <Input placeholder="pic@email.com" {...register("picEmail")} />
            </Field>

            <Field
              label="Reason/Description"
              error={errors.reasonDescription?.message}
              className="sm:col-span-2 lg:col-span-3"
            >
              <textarea
                rows={3}
                placeholder="Reason for adding this company"
                className="w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.06)] px-3 py-2.5 text-[color:rgba(255,255,255,0.92)] placeholder:text-[color:rgba(255,255,255,0.35)] outline-none transition focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)] focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]"
                {...register("reasonDescription")}
              />
            </Field>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
